import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { orderStore } from '@store/orders';
import { convertDateTimeV2 } from '@utils/utils';
import { DateTime } from 'luxon';
import * as yup from 'yup';
/*eslint-disable */
// Need to disable eslint to make function use generic type
const createConditionalSchema = <T>(
  schema: yup.Schema<T>,
  stopId: number,
  dropOffStop: boolean,
) =>
  yup.lazy((item) =>
    item.operationType === 'Delete' ||
    (dropOffStop && stopId != item.dropOffId) ||
    (!dropOffStop && stopId !== item.pickUpId)
      ? yup.object().shape({})
      : schema,
  );
/*eslint-enable */

const isLtlCleanupEnabled = isFeatureFlagEnabled(
  FeatureFlags.ltlStopModalCleanup,
);

const isMixedCommodityEnabled = isFeatureFlagEnabled(
  FeatureFlags.LtlSupportMixedCommodityPalletOnLoad,
);

// Mixed-commodity sub-items of an LTL load item. Mirrors the quote-side
// breakdown rules: weight must be a positive number and a class is required.
// Soft-deleted rows are skipped, as the parent load items are.
const loadItemBreakdownSchema = yup.object().shape({
  weight: yup
    .mixed()
    .test('is-number', 'Sub-item weight must be a number', (value) => {
      return value !== '' && value != null && !isNaN(Number(value));
    })
    .test('is-positive', 'Sub-item weight must be positive', (value) => {
      return Number(value) > 0;
    })
    .test(
      'must-be-at-most-six-digits',
      'Sub-item weight must be at most 6 digits',
      (value) => {
        return Number(value) <= 999999;
      },
    )
    .required('Sub-item weight is required'),
  class: yup
    .mixed()
    .nullable()
    .test('class-required', 'Sub-item class is required', (value) => {
      return value !== null && value !== undefined && String(value) !== '';
    }),
});

const loadItemBreakdownsSchema = yup
  .array()
  .of(
    yup.lazy((breakdown: { operationType?: string } | null | undefined) =>
      breakdown?.operationType === 'Delete'
        ? yup.object().shape({})
        : loadItemBreakdownSchema,
    ),
  )
  .nullable();

export const validations = () =>
  yup.object().shape({
    locationName: yup.string().required('Location Name is required'),
    address1: yup.string().required('Address Line 1 is required'),
    address2: yup.string(),
    countryCode: yup.string(),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    zip: yup
      .string()
      .required('Zip Code is required')
      .test('zip-validation', function (value) {
        if (!value) return true;
        if (value.length < 5)
          return this.createError({
            message: 'Zip Code must be at least 5 characters',
          });

        const zipRegex =
          /^([0-9]{5}|[0-9]{5}-[0-9]{4}|[A-Z][0-9][A-Z] [0-9][A-Z][0-9]|[A-Z][0-9][A-Z][0-9][A-Z][0-9])$/i;
        if (!zipRegex.test(value))
          return this.createError({ message: 'Invalid ZIP code format' });

        return true;
      }),
    email: yup.string().email('Invalid email').nullable(),
    phone: yup.string().when('mode', ([mode]) => {
      return mode === 'LTL'
        ? yup
            .string()
            .required('Contact Phone is required')
            .test(
              'len',
              'Phone number must be at least 10 digits',
              (val) => Boolean(val) === false || val.length >= 10,
            )
        : yup
            .string()
            .nullable()
            .test(
              'len',
              'Phone number must be at least 10 digits',
              (val) =>
                Boolean(val) === false ||
                (val != undefined && val.length >= 10),
            );
    }),

    stopDateTime: yup
      .string()
      .test('invalidDateTime', 'Invalid DateTime', function (value) {
        return value !== 'Invalid DateTime';
      })
      .required('Stop Date Time is required'),
    driverDateIn: yup.string().when('mode', ([mode]) => {
      if (mode === 'LTL') {
        const message = isLtlCleanupEnabled()
          ? 'Open date required'
          : 'Driver In Date is required';
        return yup.string().required(message);
      }
      return yup.string().nullable();
    }),
    driverDateOut: yup.string().when('mode', ([mode]) => {
      if (mode === 'LTL') {
        const message = isLtlCleanupEnabled()
          ? 'Closed date required'
          : 'Driver Out Date is required';
        return yup.string().required(message);
      }
      return yup.string().nullable();
    }),
    beginTimeRange:
      orderStore.isCreate &&
      isFeatureFlagEnabled(FeatureFlags.RequiredBeginTimeRange)()
        ? yup.number().when('mode', ([mode]) => {
            // Begin time stays optional for the TL facilities workflow and for
            // LTL when ltlStopModalCleanup is enabled.
            if (
              (mode === 'LTL' && isLtlCleanupEnabled()) ||
              (mode === 'TL' &&
                isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)())
            ) {
              return yup.number().nullable();
            }
            return yup.number().required('The begin time is required');
          })
        : yup.number().nullable(),
    appointmentSchedulingStatus: yup
      .string()
      .nullable()
      .oneOf([
        '',
        'Required',
        'ReadyToSchedule',
        'AppointmentRequested',
        'AppointmentConfirmed',
        null,
      ])
      .test('confirmed-appointment-fields', function (status) {
        if (status !== 'AppointmentConfirmed') return true;
        const { parent, createError } = this;
        const appointmentFields = parent as {
          mode?: string;
          facilityAppointmentType?: string;
          appointmentTimeZone?: string;
          beginTimeRange?: number | null;
          dateAppointment?: string;
          stopDateTime?: string;
          timeAppointment?: string;
          timezone?: string;
        };
        const facilitiesEnabled =
          appointmentFields.mode === 'TL' &&
          isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)();
        if (!facilitiesEnabled) return true;
        const usesVisibleBeginTime =
          appointmentFields.facilityAppointmentType === 'Appointment' &&
          isFeatureFlagEnabled(FeatureFlags.MilitaryTime)();
        const hasDate = Boolean(
          usesVisibleBeginTime
            ? appointmentFields.stopDateTime
            : appointmentFields.dateAppointment ||
                appointmentFields.stopDateTime,
        );
        const hasTime = usesVisibleBeginTime
          ? appointmentFields.beginTimeRange != null
          : appointmentFields.beginTimeRange != null ||
            Boolean(appointmentFields.timeAppointment);
        const hasTimezone = Boolean(
          appointmentFields.appointmentTimeZone || appointmentFields.timezone,
        );
        return hasDate && hasTime && hasTimezone
          ? true
          : createError({
              message:
                'A confirmed appointment requires a date, begin time, and time zone',
            });
      }),
    aiSchedulingEnabled: yup.boolean(),
    appointmentPreference: yup.string().max(500, 'Maximum of 500 characters'),
    driverTimeIn: yup.string().when('mode', ([mode]) => {
      return mode === 'LTL'
        ? yup.string().test({
            name: 'driverTimeIn',
            message:
              'Shipping Date/Hours (open) should be earlier than Shipping Date/Hours (close)',
            test: function (value) {
              const { parent, createError } = this;
              const { driverTimeOut, driverDateIn, driverDateOut } = parent;
              if (!Boolean(value)) {
                const timeInMsg = isLtlCleanupEnabled()
                  ? 'Open time required'
                  : 'Driver Time In is required';
                return createError({ message: timeInMsg });
              }
              if (!Boolean(driverTimeOut as string)) return true;
              const shippingHourOpen = convertDateTimeV2(
                driverDateIn as string,
                value!,
              );
              const shippingHourClose = convertDateTimeV2(
                driverDateOut as string,
                driverTimeOut as string,
              );
              if (
                DateTime.fromISO(shippingHourOpen).diff(
                  DateTime.fromISO(shippingHourClose),
                ).milliseconds < 0
              ) {
                return true;
              }
              return createError({
                message:
                  'Shipping Date/Hours (open) should be earlier than Shipping Date/Hours (close)',
              });
            },
          })
        : yup.string().nullable();
    }),
    driverTimeOut: yup.string().when('mode', ([mode]) => {
      return mode === 'LTL'
        ? yup
            .string()
            .required(
              isLtlCleanupEnabled()
                ? 'Closed time required'
                : 'Driver Time Out is required',
            )
        : yup.string().nullable();
    }),
    contact: yup.string().when('mode', ([mode]) => {
      return mode === 'LTL'
        ? yup.string().required('Contact Name is required')
        : yup.string().nullable();
    }),
    loadItems: yup
      .array()
      .when(['mode', 'id', 'dropOff'], ([mode, id, dropOff]) => {
        return mode === 'LTL'
          ? yup.array().of(
              createConditionalSchema(
                yup.object().shape({
                  //In V3 none of this stuff is required even for LTL, w/ the exception of desc.
                  class: yup.string().required('Class is required'),
                  // nmfc: yup
                  //   .string()
                  //   .max(10, 'NMFC must be at most 10 characters'),
                  // pieces: yup
                  //   .number()
                  //   .max(9999999, 'Pieces must be at most 7 characters'),
                  description: yup
                    .string()
                    .required('Description Required')
                    .max(150, 'Description must be at most 150 characters'),
                  quantity: yup
                    .mixed()
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .test(
                      'is-number-or-empty',
                      'Quantity must be a number',
                      (value) => {
                        return value === '' || !isNaN(Number(value));
                      },
                    )
                    .test(
                      'is-decimal',
                      'Quantity cannot be a decimal',
                      (value) => {
                        return value && !value.toString().includes('.');
                      },
                    )
                    .test(
                      'must-be-at-least-999',
                      'Quantity must be less than 99999',
                      (value) => {
                        return Number(value) <= 99999;
                      },
                    )
                    .nullable()
                    .required('Quantity is required'),
                  weight: yup
                    .mixed()
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .test(
                      'is-number-or-empty',
                      'weight must be a number',
                      (value) => {
                        if (value === '') {
                          return false;
                        }
                        return !isNaN(Number(value));
                      },
                    )
                    .test('is-positive', 'Weight must be positive', (value) => {
                      return Number(value) > 0;
                    })
                    .test(
                      'must-be-at-least-999',
                      'Weight must be at most 6 digits',
                      (value) => {
                        return Number(value) <= 999999;
                      },
                    )
                    .required('Weight is required'),
                  // Sub-items are only editable behind the mixed-commodity
                  // flag; with it off the stop form validates exactly as before.
                  loadItemBreakdowns: isMixedCommodityEnabled()
                    ? loadItemBreakdownsSchema
                    : yup.mixed().nullable(),
                  // lengthInch: yup
                  //   .number()
                  //   .typeError('Length inches must be a number')
                  //   .positive('Length inches must be a positive number')
                  //   .max(999, 'Length inches must be at most 3 digits')
                  //   .required('Length inches is required'),
                  // heightInch: yup
                  //   .number()
                  //   .typeError('Height inches must be a number')
                  //   .positive('Height inches must be a positive number')
                  //   .max(999, 'Height inches must be at most 3 digits')
                  //   .required('Height inches is required'),
                  // widthInch: yup
                  //   .number()
                  //   .typeError('Width inches must be a number')
                  //   .positive('Width inches must be a positive number')
                  //   .max(999, 'Width inches must be at most 3 digits')
                  //   .required('Width inches is required'),
                }),
                id as number,
                dropOff as boolean,
              ),
            )
          : yup.array().of(
              createConditionalSchema(
                yup.object().shape({
                  // length: yup
                  //   .number()
                  //   .positive('Length must be a positive number')
                  //   .required('Length is required'),
                  // height: yup
                  //   .number()
                  //   .positive('Height must be a positive number')
                  //   .required('Height is required'),
                  // width: yup
                  //   .number()
                  //   .positive('Width must be a positive number')
                  //   .required('Width is required'),
                  quantity: yup
                    .mixed()
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .test(
                      'is-number-or-empty',
                      'Quantity must be a number',
                      (value) => {
                        return value === '' || !isNaN(Number(value));
                      },
                    )
                    .test(
                      'is-decimal',
                      'Quantity cannot be a decimal',
                      (value) => {
                        return value && !value.toString().includes('.');
                      },
                    )
                    .nullable()
                    .required('Quantity is required'),
                  weight: yup
                    .mixed()
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .test(
                      'is-number-or-empty',
                      'weight must be a number',
                      (value) => {
                        if (value === '') {
                          return false;
                        }
                        return !isNaN(Number(value));
                      },
                    )
                    .test('is-positive', 'Weight must be positive', (value) => {
                      return Number(value) >= 0;
                    })
                    .test(
                      'must-be-at-least-999',
                      'Weight must be at most 6 digits',
                      (value) => {
                        return Number(value) <= 999999;
                      },
                    ),
                  // lengthInch: yup
                  //   .number()
                  //   .typeError('Length inches must be a number')
                  //   .positive('Length inches must be a positive number')
                  //   .max(999, 'Length inches must be at most 3 digits')
                  //   .required('Length inches is required'),
                  // heightInch: yup
                  //   .number()
                  //   .typeError('Height inches must be a number')
                  //   .positive('Height inches must be a positive number')
                  //   .max(999, 'Height inches must be at most 3 digits')
                  //   .required('Height inches is required'),
                  // widthInch: yup
                  //   .number()
                  //   .typeError('Width inches must be a number')
                  //   .positive('Width inches must be a positive number')
                  //   .max(999, 'Width inches must be at most 3 digits')
                  //   .required('Width inches is required'),
                }),
                id as number,
                dropOff as boolean,
              ),
            );
      }),
    dateAppointment: yup
      .string()
      .nullable()
      .test('invalid-date', 'Invalid date format', (value) => {
        if (value == null || value === '') return true;
        const date = DateTime.fromISO(value);
        return date.isValid && date.year >= 2000;
      }),
  });

export const HazardousMaterialSchema = yup.object().shape({
  unNumber: yup
    .string()
    .required('UN/NA Number is required')
    .matches(
      /^(UN|NA)\d{4}$/,
      'UN/NA Number must start with "UN" or "NA" followed by exactly 4 digits',
    ),
  packingGroup: yup.string().nullable(),
  hazardClass: yup.string().nullable(),
  piece: yup.string().nullable(),
  contactNumber: yup
    .string()
    .required('Contact Number is required')
    .min(10, 'Phone number must be at least 10 digits'),
  contactName: yup.string().required('Contact Name is required'),
});
